import { Divider, Stack } from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { ChangeEntry, MissingItem } from '../state';

import { ChangeList } from './ChangeList';
import type { ChangeListProps } from './ChangeList';
import { PanelSection } from './PanelSection';
import { WhatsMissing } from './WhatsMissing';

/**
 * The right panel, open (brief §3): signals only — What's missing, then
 * What's changed, each a section as the library's panel layout (P3) draws
 * one, with a divider between. Opening it counts as seeing the changes, so the rail's dot
 * goes.
 */
export type DetailsPanelProps = Omit<ChangeListProps, 'changes'> & {
  missing: MissingItem[];
  changes: ChangeEntry[];
  onGoMissing: (item: MissingItem) => void;
  onSeen: () => void;
};

export const DetailsPanel = ({
  missing,
  changes,
  onGoMissing,
  onSeen,
  ...list
}: DetailsPanelProps) => {
  const { t } = useTranslation('pmDetails');
  // Mounted only while open (Panels.Drawer unmounts collapsed content), and
  // again on every new change while open: what is on screen has been seen.
  useEffect(() => {
    onSeen();
  }, [onSeen, changes.length]);
  return (
    <Stack sx={{ gap: 3 }} data-automation-id="details-panel">
      <PanelSection icon="flag" title={t('panel.Missing')} automationId="details-section-missing">
        <WhatsMissing missing={missing} onGo={onGoMissing} />
      </PanelSection>
      <Divider sx={{ borderColor: 'var(--color-fog-20)' }} />
      <PanelSection
        icon="history"
        title={t('panel.Changed')}
        automationId="details-section-changed"
      >
        <ChangeList changes={changes} {...list} />
      </PanelSection>
    </Stack>
  );
};
